import { useState, useEffect, useCallback } from 'react';
import { maintenanceService } from '../services/maintenanceService.ts';
import { MaintenanceRecord, MaintenanceFormData } from '../types/maintenance.ts';

export const useMaintenanceRecords = (propertyId: string) => {
  const [records, setRecords] = useState<MaintenanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      const data = await maintenanceService.fetchRecords(propertyId);
      setRecords(data);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const addRecord = async (recordData: MaintenanceFormData) => {
    try {
      const newRecord = await maintenanceService.createRecord(recordData);
      setRecords([newRecord, ...records]);
      return newRecord;
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  const updateRecord = async (id: string, recordData: Partial<MaintenanceFormData>) => {
    try {
      const updatedRecord = await maintenanceService.updateRecord(id, recordData);
      setRecords(records.map(record => 
        record.id === id ? updatedRecord : record
      ));
      return updatedRecord;
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  const deleteRecord = async (id: string) => {
    try {
      await maintenanceService.deleteRecord(id);
      setRecords(records.filter(record => record.id !== id));
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  return {
    records,
    loading,
    error,
    addRecord,
    updateRecord,
    deleteRecord,
    refresh: fetchRecords
  };
};
